/**
 * API client — the single entry point for every HTTP call made by the
 * admin SPA.
 *
 * Invariants:
 *   1. Component code never calls `apiFetch` directly. All calls go
 *      through a typed TanStack Query hook in `features/<feature>/
 *      queries.ts` or the top-level `queries/`. This is enforced by
 *      lint rules in code review, not by the type system.
 *   2. Cookies are the only auth channel — the HttpOnly session
 *      cookie is sent via `credentials: "include"`. Tokens never live
 *      in JS, localStorage, or headers.
 *   3. Every request carries an `X-Request-ID` so server logs can be
 *      correlated to a single user click. The server echoes it back
 *      on error responses, and `ApiError` surfaces it so UI code can
 *      show it in a toast for support escalation.
 *   4. Failures always throw a typed subclass of `ApiError`. No
 *      call site does `if (res.ok)` — they let the error hit
 *      TanStack Query's `error` channel.
 *
 * Error taxonomy:
 *   - ApiError              — base class; carries status, body, requestId, message
 *   - AuthError             — 401; auth guard redirects to /login
 *   - StepUpRequiredError   — 403 with `{ error: "step_up_required", ... }`
 *                             caught by `withStepUp()` which runs the
 *                             assertion ceremony and retries the request
 *   - ForbiddenError        — 403 without step-up (permission denied)
 *   - ValidationError       — 422; carries structured field errors
 *   - NotFoundError         — 404
 *   - ServerError           — 5xx; TanStack Query retries twice
 */
import { getAdminApiUrl } from "@pureid/ui";

export const API_BASE: string = getAdminApiUrl();

/**
 * Rewrite platform-scoped paths to the tenant-host equivalent when
 * the SPA is running on a tenant subdomain. The admin SPA bundle is
 * served from both `console.{base_domain}` (platform admins,
 * cross-tenant) and the bare tenant root `{slug}.{base_domain}`
 * (the tenant-admin console — the tenant-scope twin of the
 * platform host, which also serves the /enroll invite wizard). The
 * same feature code calls `/api/v1/tenants/{tid}/{resource}`
 * regardless of host because that's the only path shape the feature
 * folders were written against; this rewriter maps it onto
 * `/api/tenant/v1/{resource}` when the request originates from a
 * tenant host so the tenant-host backend tree (which host-resolves
 * the tenant from the request `Host:` header and gates on tenant
 * roles) handles it.
 *
 * Platform admin sessions on a tenant host still get the right
 * semantics — the tenant-host mount accepts platform_admin as well
 * as tenant_admin per the existing RBAC tables — so the rewrite
 * is safe for both actor types.
 *
 * The rewriter is a no-op:
 *   - on `console.{base_domain}` (platform host, matches nothing)
 *   - for non-platform paths (pass-through)
 *   - in non-browser environments (SSR/tests, window is undefined)
 *
 * This keeps all feature code host-agnostic — individual query
 * files continue to target the canonical platform path shape and
 * never branch on host.
 */
function isTenantHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.host.toLowerCase();
  // The admin SPA lives on console.{base_domain}; anything else
  // is a tenant host. The rewriter ONLY flips for tenant hosts,
  // so a mistaken "no match" on the platform host is the safe
  // default (keeps the platform path shape).
  return !host.startsWith("console.");
}

/**
 * True on the platform console (`console.{base_domain}`), where requests keep the
 * `/api/v1/tenants/{tenant_id}` platform shape. A control whose route is served on the platform
 * tree only (no tenant-host twin, by design) is offered only here, so it is never a button that
 * answers 404 on a tenant host.
 */
export function isPlatformConsoleHost(): boolean {
  return typeof window !== "undefined" && !isTenantHost();
}

export function rewritePathForHost(path: string): string {
  if (!path.startsWith("/api/v1/tenants/")) return path;
  if (!isTenantHost()) return path;
  // Strip `/api/v1/tenants/{tenant_id}` and prepend
  // `/api/tenant/v1`. The tenant_id segment is redundant on a
  // tenant host because the tenantResolver pulls it from the
  // Host header; dropping it aligns with the non-path-scoped
  // tenant-host routes.
  const prefix = "/api/v1/tenants/";
  const rest = path.slice(prefix.length);
  const slash = rest.indexOf("/");
  if (slash < 0) {
    // Bare `/api/v1/tenants/{id-or-slug}` — the tenant-detail
    // fetch itself. The tenant-host equivalent is the caller's-
    // own-tenant endpoint `GET /api/tenant/v1/tenant`
    // (handler_tenant.go), which returns the identical
    // TenantResponse shape (same tenantToResponse +
    // attachCounts path as the platform handler).
    return "/api/tenant/v1/tenant";
  }
  return `/api/tenant/v1${rest.slice(slash)}`;
}

export function resolveApiPath(path: string, preservePlatformPath = false): string {
  return preservePlatformPath ? path : rewritePathForHost(path);
}

// ── Error hierarchy ────────────────────────────────────────────────────────
// The base classes live in `@pureid/auth-core` so the portal shares
// them. Admin adds two more: `StepUpRequiredError` (the WebAuthn
// step-up signal) and `ValidationError` (structured 422 responses).
// Both chain back to the core `ApiError` so typed catch blocks in
// shared code only have to know about the base class.

export {
  ApiError,
  AuthError,
  ForbiddenError,
  NotFoundError,
  ServerError,
  StepUpFailedError,
} from "@pureid/auth-core";
import { ApiError, AuthError, ForbiddenError, NotFoundError, ServerError, StepUpFailedError } from "@pureid/auth-core";

/**
 * Thrown when the backend demands a fresh WebAuthn assertion before
 * the current operation is allowed to proceed. `withStepUp()` catches
 * this, runs the step-up ceremony, and retries the original request.
 *
 * The backend emits RFC 9457 Problem Details with
 * `type: ".../step-up-required"` and no challenge identifier —
 * step-up is a fresh discoverable assertion against the tenant's
 * RP, not a server-issued nonce. The `reason` is derived from the
 * problem's `detail` field for surfacing in the dialog.
 */
export class StepUpRequiredError extends ApiError {
  constructor(
    public readonly reason: string,
    body: unknown,
    requestId: string | null,
  ) {
    super(403, body, requestId, `step-up required: ${reason}`);
    this.name = "StepUpRequiredError";
  }
}

export interface ValidationFieldError {
  field: string;
  message: string;
}

export class ValidationError extends ApiError {
  constructor(
    public readonly fields: ValidationFieldError[],
    body: unknown,
    requestId: string | null,
    message: string,
  ) {
    super(422, body, requestId, message);
    this.name = "ValidationError";
  }
}

/**
 * Thrown on a 412 Precondition Failed response — the server is
 * telling us the row's `updated_at` no longer matches the
 * `If-Unmodified-Since` we sent, i.e. another admin saved the
 * tenant section since the form loaded. UI should refetch the
 * tenant and prompt the operator before retrying.
 */
export class PreconditionFailedError extends ApiError {
  constructor(body: unknown, requestId: string | null, message: string) {
    super(412, body, requestId, message);
    this.name = "PreconditionFailedError";
  }
}

/** One colliding value blocking a UniquenessConflictError's toggle from enabling. */
export interface UniquenessConflictItem {
  value: string;
  count: number;
  user_ids: string[];
}

/**
 * Thrown on a 409 response carrying `code: "uniqueness-conflict"` —
 * today only the tenant identity-matching toggles
 * (require_unique_employee_number / require_unique_primary_email)
 * produce this. `duplicates` is the itemized, capped list of
 * colliding values the backend found; the settings page renders it
 * as a resolution dialog rather than a generic toast, since a bare
 * error message gives the admin no way to act on it.
 */
export class UniquenessConflictError extends ApiError {
  constructor(
    public readonly field: string,
    public readonly duplicates: UniquenessConflictItem[],
    body: unknown,
    requestId: string | null,
    message: string,
  ) {
    super(409, body, requestId, message);
    this.name = "UniquenessConflictError";
  }
}

// ── Options ────────────────────────────────────────────────────────────────

type SearchValue = string | number | boolean | null | undefined;

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON body. Serialized with JSON.stringify — do not pre-stringify. */
  body?: unknown;
  /** Tenant slug header. Added as `X-Tenant-Slug` when present. */
  tenantSlug?: string;
  /** Keep a provider-only `/api/v1/tenants/...` path on tenant hosts.
   * Use only for endpoints deliberately absent from `/api/tenant/v1`. */
  preservePlatformPath?: boolean;
  /**
   * Optional client-generated ceremony correlation UUID, sent as
   * `X-Ceremony-ID`. When set, the server stamps this on every
   * structured-log line and audit row produced by the request, and
   * any related `/api/client-telemetry` events from the same
   * onboarding attempt join cleanly under the same key.
   *
   * Use one id per onboarding attempt (mint at surface mount via
   * `createCeremonyId()` from `@pureid/auth-core`); pass it to every
   * apiFetch call that participates in the same ceremony. The
   * value is purely a join key, never an auth credential — the
   * server validates UUID shape and otherwise treats it as opaque
   * metadata.
   */
  ceremonyId?: string;
  /**
   * Query string parameters. Arrays are serialized as repeated keys
   * (`?tag=a&tag=b`). Null and undefined values are dropped.
   */
  searchParams?: Record<string, SearchValue | readonly SearchValue[]>;
  /**
   * Abort the request if it takes longer than this many milliseconds.
   * Defaults to 30 seconds. Set to 0 to disable.
   */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;

// ── Request ID ─────────────────────────────────────────────────────────────

function generateRequestId(): string {
  // 16 hex chars (64 bits) — enough entropy for correlation, short
  // enough to paste into a Slack thread. crypto.randomUUID is the
  // standard path; the fallback covers old webviews in case this ever
  // runs outside a modern browser.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 16);
  }
  return Math.random().toString(16).slice(2, 18).padEnd(16, "0");
}

// ── Query string serialization ─────────────────────────────────────────────

function serializeSearchParams(
  input: Record<string, SearchValue | readonly SearchValue[]>,
): string {
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(input)) {
    if (raw === undefined || raw === null || raw === "") continue;
    if (Array.isArray(raw)) {
      for (const v of raw) {
        if (v !== undefined && v !== null && v !== "") {
          params.append(key, String(v));
        }
      }
    } else {
      params.set(key, String(raw));
    }
  }
  return params.toString();
}

// ── Error construction ────────────────────────────────────────────────────

function extractMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    // Order matters and so does the emptiness check. RFC 9457 `title` is a
    // short summary and `detail` is the specific one, so detail wins — but a
    // present-and-EMPTY detail used to win too, because typeof "" === "string".
    // That returned "", not the fallback, so the caller's
    // toast.error(err.message) rendered an empty toast: a failure the user
    // could see happen and not read.
    //
    // Terse problems are common on purpose (xhttp.WriteError takes title and
    // detail separately, and several handlers say it all in the title and pass
    // "" for detail), so falling through to title is what makes those legible
    // instead of "409 Conflict" or nothing at all.
    for (const key of ["error", "message", "detail", "title"] as const) {
      const v = obj[key];
      if (typeof v === "string" && v.trim() !== "") return v;
    }
  }
  return fallback;
}

function extractStepUp(payload: unknown): { reason: string } | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;
  // Backend emits Problem Details with EITHER `type` ending
  // in "step-up-required" OR `code` === "step-up-required".
  // Matching both keeps the wrapper robust if the backend
  // ever drops the well-known type URL (the `code` field is
  // the more authoritative signal).
  const type = typeof obj.type === "string" ? obj.type : "";
  const code = typeof obj.code === "string" ? obj.code : "";
  if (!type.endsWith("step-up-required") && code !== "step-up-required") {
    return null;
  }
  const reason = typeof obj.detail === "string" ? obj.detail : "policy_required";
  return { reason };
}

// Set of stable error codes the stepup_handler emits when the
// VERIFY endpoint rejects the supplied assertion. These are
// recoverable (the user's MAIN session is still valid; only the
// step-up ceremony failed) — we lift them out of the generic
// 401/403 buckets so SignedOutScreen does NOT fire.
//
// Source: backend/internal/identity/session/stepup_handler.go
//   - "stepup-rate-limited"        (429) — too many attempts
//   - "stepup-assertion-rejected"  (401) — assertion verify failed
//   - "stepup-user-mismatch"       (403) — user-binding guard
const STEPUP_FAILURE_CODES = new Set<string>([
  "stepup-user-verification-required",
  "stepup-assertion-rejected",
  "stepup-user-mismatch",
  "stepup-rate-limited",
]);

function extractStepUpFailureCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;
  const code = typeof obj.code === "string" ? obj.code : "";
  return STEPUP_FAILURE_CODES.has(code) ? code : null;
}

function extractValidationFields(payload: unknown): ValidationFieldError[] {
  if (!payload || typeof payload !== "object") return [];
  const obj = payload as Record<string, unknown>;
  // The canonical RFC 9457 Problem shape (xhttp.WriteValidationError)
  // carries field errors under `errors`. The legacy governance 422
  // shape (admin_handler.go / org_policy_handler.go) carries them
  // under `fields`. Read both so every 422 surface populates
  // ValidationError.fields the same way; the rest of the SPA can
  // walk fields → RHF setError / inline copy without caring which
  // handler produced the response.
  const raw = Array.isArray(obj.errors)
    ? obj.errors
    : Array.isArray(obj.fields)
      ? obj.fields
      : [];
  return raw
    .filter((f): f is Record<string, unknown> => !!f && typeof f === "object")
    .map((f) => ({
      field: typeof f.field === "string" ? f.field : "",
      message: typeof f.message === "string" ? f.message : "invalid",
    }));
}

function extractUniquenessConflict(
  payload: unknown,
): { field: string; duplicates: UniquenessConflictItem[] } | null {
  if (!payload || typeof payload !== "object") return null;
  const obj = payload as Record<string, unknown>;
  if (obj.code !== "uniqueness-conflict") return null;
  const field = typeof obj.field === "string" ? obj.field : "";
  const rawDuplicates = Array.isArray(obj.duplicates) ? obj.duplicates : [];
  const duplicates = rawDuplicates
    .filter((d): d is Record<string, unknown> => !!d && typeof d === "object")
    .map((d) => ({
      value: typeof d.value === "string" ? d.value : "",
      count: typeof d.count === "number" ? d.count : 0,
      user_ids: Array.isArray(d.user_ids) ? d.user_ids.filter((id): id is string => typeof id === "string") : [],
    }));
  return { field, duplicates };
}

function buildError(
  status: number,
  body: unknown,
  requestId: string | null,
  statusText: string,
): ApiError {
  const message = extractMessage(body, `${status} ${statusText}`);
  // Step-up detection runs BEFORE the generic 401 / 403 branches
  // because the backend's step-up gate returns 401 (see
  // backend/internal/platform/middleware/auth.go — the canonical
  // status for "your current credentials are insufficient,
  // re-authenticate"), while older readers might expect 403
  // ("you're authenticated but not permitted"). Either status
  // with Problem Details `type` ending in "step-up-required" is
  // a step-up challenge, and withStepUp() needs a
  // StepUpRequiredError to open the ceremony dialog. Without
  // this guard the 401 fell through to AuthError and every
  // step-up-gated mutation (tenant create, admin invite, saml
  // manage, recovery, etc.) appeared to silently fail — the
  // dialog would submit, the error would flow into the toast,
  // and no passkey prompt would ever show.
  const stepUp = extractStepUp(body);
  if (stepUp && (status === 401 || status === 403)) {
    return new StepUpRequiredError(stepUp.reason, body, requestId);
  }
  // Stepup VERIFY failure codes — recoverable. The user's main
  // session is still valid; only the step-up ceremony failed
  // (wrong passkey, bad signature, user-binding mismatch, rate
  // limit). Without this branch, the 401-coded "stepup-assertion-
  // rejected" fell through to AuthError → SignedOutScreen, which
  // looked to the user like "I picked the wrong passkey, the
  // platform signed me out." See backend stepup_handler.go for
  // the response shape.
  const stepUpFailureCode = extractStepUpFailureCode(body);
  if (stepUpFailureCode) {
    return new StepUpFailedError(stepUpFailureCode, status, body, requestId, message);
  }
  if (status === 401) return new AuthError(body, requestId, message);
  if (status === 403) return new ForbiddenError(body, requestId, message);
  if (status === 404) return new NotFoundError(body, requestId, message);
  if (status === 409) {
    const conflict = extractUniquenessConflict(body);
    if (conflict) {
      return new UniquenessConflictError(conflict.field, conflict.duplicates, body, requestId, message);
    }
  }
  if (status === 412) return new PreconditionFailedError(body, requestId, message);
  if (status === 422) {
    return new ValidationError(extractValidationFields(body), body, requestId, message);
  }
  if (status >= 500) return new ServerError(status, body, requestId, message);
  return new ApiError(status, body, requestId, message);
}

// ── Core fetch ─────────────────────────────────────────────────────────────

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const {
    body,
    tenantSlug,
    preservePlatformPath = false,
    ceremonyId,
    searchParams,
    headers,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    signal: externalSignal,
    ...rest
  } = options;

  const rewritten = resolveApiPath(path, preservePlatformPath);
  const customer = path.match(/^\/api\/v1\/tenants\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?]|$)/i)?.[1];

  let url = `${API_BASE}${rewritten}`;
  if (searchParams) {
    const qs = serializeSearchParams(searchParams);
    if (qs) url += `?${qs}`;
  }

  const requestId = generateRequestId();

  // Compose caller's AbortSignal with the timeout so either can cancel.
  const controller = new AbortController();
  const timeoutHandle =
    timeoutMs > 0
      ? setTimeout(() => controller.abort(new DOMException("Timeout", "TimeoutError")), timeoutMs)
      : null;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener("abort", () => controller.abort(externalSignal.reason));
  }

  // FormData requests go out as multipart/form-data with a
  // browser-generated boundary — the browser sets Content-Type
  // automatically and forcing application/json would break the
  // server's multipart parser. Detect and skip the default
  // content-type + JSON.stringify when the caller passed a
  // FormData body.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      credentials: "include",
      // Never let the HTTP cache answer an API call. The server sends
      // Cache-Control: no-store (see middleware.SecurityHeaders), but a
      // response the browser stored BEFORE that header existed stays
      // eligible for heuristic reuse, and heuristic reuse is invisible:
      // the request is answered from disk without ever reaching us, so
      // the staleness shows up in neither our access log nor our metrics.
      // Chrome and Firefox disagree on how aggressively they do this,
      // which is exactly how one browser can be stuck on a deleted
      // tenant's id while another is fine. Freshness for API data is
      // TanStack Query's job (staleTime / invalidation), not the HTTP
      // cache's — this makes that division of labour explicit.
      cache: "no-store",
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        "X-Request-ID": requestId,
        ...(tenantSlug ? { "X-Tenant-Slug": tenantSlug } : {}),
        ...(customer && rewritten !== path ? { "X-Managed-Tenant-ID": customer } : {}),
        ...(ceremonyId ? { "X-Ceremony-ID": ceremonyId } : {}),
        ...headers,
      },
      body: isFormData
        ? (body as FormData)
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
      signal: controller.signal,
    });
  } finally {
    if (timeoutHandle !== null) clearTimeout(timeoutHandle);
  }

  const responseRequestId = res.headers.get("x-request-id") ?? requestId;

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  // Accept both application/json (success bodies) and RFC 9457
  // application/problem+json (auth / step-up / validation errors).
  // Without the +json suffix the step-up 401 body is read as a string
  // and extractStepUp can't see the `type: ".../step-up-required"`
  // field, so every step-up-gated mutation (tenant create, admin
  // invite, recovery, …) throws AuthError instead of opening the
  // step-up dialog.
  const isJson = /^application\/(problem\+)?json\b/i.test(contentType);
  const payload: unknown = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => "");

  if (!res.ok) {
    throw buildError(res.status, payload, responseRequestId, res.statusText);
  }

  return payload as T;
}
